import { supabase } from '../../shared/supabase';
import { runAuthOperation, signOutSafely, withTimeout } from '../utils/authLockUtils';

export const DEFAULT_AVATAR_URL =
  'https://vogixryubhpqziivspks.supabase.co/storage/v1/object/public/avatars/default.jpg';

const AUTH_RATE_LIMIT_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/auth-rate-limit`;

class AuthService {
  constructor() {
    this.DEFAULT_AVATAR_URL = DEFAULT_AVATAR_URL;
    this.AUTH_RATE_LIMIT_URL = AUTH_RATE_LIMIT_URL;
  }

  /**
   * Route auth actions through the rate-limited edge function.
   * @private
   */
  async _callAuthEdgeFunction(action, payload) {
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const res = await fetch(this.AUTH_RATE_LIMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ action, ...payload }),
        signal: controller.signal,
      });
      if (res.status === 429) {
        const json = await res.json().catch(() => ({}));
        const retryAfter = json.retryAfter || 60;
        throw Object.assign(
          new Error(`Too many attempts. Please wait ${retryAfter} seconds before trying again.`),
          { status: 429, retryAfter },
        );
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || json.message || 'Authentication failed');
      }
      return json;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Sign-in request timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Check if current environment is localhost.
   * @private
   */
  _isLocalDevHost() {
    return (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    );
  }

  /**
   * Direct password sign in via GoTrue.
   * @private
   */
  async _signInWithPasswordDirect(email, password) {
    const { data, error } = await withTimeout(
      supabase.auth.signInWithPassword({ email, password }),
      15000,
      'Sign-in timed out',
    );
    if (error) throw error;
    return { session: data.session, user: data.user };
  }

  /**
   * Sync Supabase auth user meta fields with user profile database.
   */
  async syncProfile(user, extraFields = {}) {
    const metadata = user.user_metadata || {};
    const googleAvatar = metadata.avatar_url || metadata.picture;

    const { error } = await supabase.from('profiles').upsert([
      {
        id: user.id,
        full_name: metadata.full_name || metadata.name || user.email,
        email: user.email,
        avatar_url: googleAvatar || this.DEFAULT_AVATAR_URL,
        ...extraFields,
      },
    ]);

    if (error) console.error('Error syncing profile:', error);
  }

  /**
   * Get the current authenticated user
   */
  async getCurrentUser() {
    const { data, error } = await runAuthOperation(() => supabase.auth.getUser(), {
      maxAttempts: 3,
      timeoutMs: 10000,
      timeoutMessage: 'Session check timed out',
    });
    if (error) throw error;
    return data.user;
  }

  /**
   * Get complete user profile
   */
  async getProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select()
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if the user profile is complete
   */
  async isProfileComplete(userId) {
    try {
      const profile = await this.getProfile(userId);
      return Boolean(profile?.mobile_number && profile?.date_of_birth);
    } catch (error) {
      return false;
    }
  }

  /**
   * Sign in with email and password
   */
  async signInWithPassword(email, password) {
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (this._isLocalDevHost()) {
      return this._signInWithPasswordDirect(trimmedEmail, trimmedPassword);
    }

    try {
      await withTimeout(
        this._callAuthEdgeFunction('sign_in', {
          email: trimmedEmail,
          password: trimmedPassword,
        }),
        12000,
        'Sign-in timed out',
      );
    } catch (edgeError) {
      if (edgeError?.status === 429) throw edgeError;
      const msg = edgeError?.message || '';
      if (/invalid login credentials|too many attempts/i.test(msg)) throw edgeError;
      console.warn('[authService] Edge sign-in failed, using direct auth:', msg);
    }

    return this._signInWithPasswordDirect(trimmedEmail, trimmedPassword);
  }

  /**
   * Complete password reset with token from email
   */
  async completePasswordReset(newPassword) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      return {
        success: true,
        message: 'Password updated successfully',
        user: data.user,
      };
    } catch (error) {
      throw new Error('Failed to update password. The link may have expired.');
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth-callback`,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign up with email and password
   */
  async signUp(userData) {
    try {
      const { fullName, email, password, mobileNumber, dateOfBirth, avatar_url } = userData;
      const {
        data: { user },
        error: authError,
      } = await runAuthOperation(
        () =>
          supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                full_name: fullName,
                mobile_number: mobileNumber,
                date_of_birth: dateOfBirth,
              },
            },
          }),
        { timeoutMs: 20000, timeoutMessage: 'Sign-up timed out' },
      );

      if (authError) throw authError;
      if (!user) throw new Error('User creation failed');

      const { error: profileError } = await supabase.from('profiles').upsert([
        {
          id: user.id,
          full_name: fullName,
          email,
          mobile_number: mobileNumber,
          date_of_birth: dateOfBirth,
          avatar_url: avatar_url || this.DEFAULT_AVATAR_URL,
        },
      ]);

      if (profileError) throw profileError;
      return user;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send an OTP to a phone number
   */
  async signInWithPhone(phone) {
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        phone: phone.replace(/\s/g, ''),
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify an OTP for a phone number
   */
  async verifyOtp(phone, token) {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phone.replace(/\s/g, ''),
        token,
        type: 'sms',
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign up with Google
   */
  async signUpWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth-callback`,
          queryParams: {
            prompt: 'consent',
            screen_hint: 'signup',
            include_granted_scopes: 'true',
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Complete or update user profile
   */
  async updateProfile(profileData, authUser) {
    try {
      const user = authUser || (await this.getCurrentUser());
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase.from('profiles').upsert({
        id: user.id,
        full_name: user.user_metadata?.full_name || user.user_metadata?.name,
        email: user.email,
        ...profileData,
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email) {
    return this._callAuthEdgeFunction('forgot_password', {
      email: email.trim(),
      redirectTo: `${window.location.origin}/reset-password`,
    });
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(email) {
    return this._callAuthEdgeFunction('forgot_password', {
      email: email.trim(),
      redirectTo: `${window.location.origin}/reset-password`,
    });
  }

  /**
   * Verify password reset token validity
   */
  async verifyPasswordResetToken(token) {
    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser(token);
      return { isValid: !error, user };
    } catch (error) {
      return { isValid: false };
    }
  }

  /**
   * Update password with reset token
   */
  async updatePasswordWithToken(newPassword, token) {
    try {
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: token,
        refresh_token: '',
      });

      if (sessionError) {
        throw sessionError;
      }

      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    return signOutSafely();
  }

  /**
   * Check if an email is already registered using RPC (more secure)
   */
  async checkEmailExistsRPC(email) {
    try {
      const trimmedEmail = email.trim().toLowerCase();

      const { data, error } = await supabase.rpc('check_email_exists', {
        email_to_check: trimmedEmail,
      });

      if (error) {
        console.error('RPC error:', error);
        return { exists: false };
      }

      return { exists: data || false };
    } catch (error) {
      console.error('Error in checkEmailExistsRPC:', error);
      return { exists: false };
    }
  }
}

// Instantiate the singleton
export const authService = new AuthService();

// Backwards-compatible bindings
export const syncProfile = authService.syncProfile.bind(authService);

export default authService;
